import { useEffect } from 'react';
import type { MarkShown } from '../hooks/useMarkSeen';

/**
 * SPEC-GAP: §6 lists Button, Chip, Card, FactRow, ParticipantChip, ComplianceNotice,
 * AnnouncementCard, MessageBubble and EmptyState — no "new" marker. Per §10 ("prefer the nearest
 * specified pattern") this is built from existing tokens only: the announcement eyebrow's own ember
 * text colour and the caller's type scale. No new colour, size or radius.
 *
 * ---
 *
 * **New** — this announcement or event is new to the person reading it (TROOP-268). The server
 * decides that, per row and per reader; this only draws it.
 *
 * **A word, not a dot.** `UnreadDot` already means *there are messages here you have not read*, and
 * an announcement card used to carry it for its replies alone — so a dot here would be one mark
 * meaning two things on the same card. The word carries the meaning and colour only accompanies it,
 * so stripping the hue changes nothing (the never-colour-alone rule).
 *
 * **Drawing it is what marks it seen**, when the caller passes `onShown` — which only the
 * noticeboard, the agenda and an event's own page do. The marker stays for this visit.
 */
export function NewMarker({
  kind,
  id,
  onShown,
}: {
  kind: 'announcement' | 'event';
  id: string;
  /** Absent on the calendar and in search: those report what is new and never clear it. */
  onShown?: MarkShown;
}) {
  useEffect(() => {
    onShown?.(kind, id);
  }, [onShown, kind, id]);

  return (
    <span
      style={{
        color: 'var(--color-accent-announcement-text)',
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      New
    </span>
  );
}
